<template>
  <div class="results-container">
    <div class="results-header">
      <h2>Результаты социометрии</h2>
      <div class="result-meta">
        <span>Дата: {{ formatDate(result.survey_date) }}</span>
        <span>Проводил: {{ result.survey_conducted_by }}</span>
      </div>
    </div>

    <div v-if="notPassed.length" class="not-passed">
      <div class="not-passed__label">Не прошли:</div>
      <div class="not-passed__list">
        <span v-for="p in notPassed" :key="p.id" class="not-passed__chip">{{ p.full_name }}</span>
      </div>
    </div>

    <!-- Вкладки результатов -->
    <div class="result-tabs">
      <button 
        v-for="tab in ['indices', 'classification', 'sociogram']"
        :key="tab"
        :class="{ active: activeResultTab === tab }"
        @click="activeResultTab = tab"
        class="result-tab-btn"
      >
        {{ tabLabels[tab] }}
      </button>
    </div>

    <!-- TAB: Личные индексы -->
    <div v-if="activeResultTab === 'indices'" class="result-content">
      <h3>Персональные индексы социометрического статуса (ПСИ)</h3>
      <div class="indices-table">
        <table>
          <thead>
            <tr>
              <th>ФИО</th>
              <th title="Индекс социометрического статуса">Ист</th>
              <th title="Индекс потребности в общении">Ипо</th>
              <th title="Индекс психологической совместимости">Исм</th>
              <th>Получено +</th>
              <th>Получено -</th>
              <th>Взаимные +</th>
              <th>Взаимные -</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="participant in survey.participants" :key="participant.id">
              <td class="name-cell">{{ participant.full_name }}</td>
              <td :class="getIndexClass(result.personal_indices[participant.id]?.status)">
                {{ result.personal_indices[participant.id]?.status.toFixed(2) }}
              </td>
              <td>{{ result.personal_indices[participant.id]?.communication.toFixed(2) }}</td>
              <td>{{ result.personal_indices[participant.id]?.compatibility.toFixed(2) }}</td>
              <td>{{ result.personal_indices[participant.id]?.plus_received }}</td>
              <td>{{ result.personal_indices[participant.id]?.minus_received }}</td>
              <td class="mutual">{{ result.personal_indices[participant.id]?.mutual_plus }}</td>
              <td class="mutual">{{ result.personal_indices[participant.id]?.mutual_minus }}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="group-indices">
        <h3>Групповые индексы социометрического статуса (ГСИ)</h3>
        <div class="indices-cards">
          <div class="index-card">
            <div class="index-label">Индекс группового единства (Иге)</div>
            <div class="index-value" :style="{ color: '#22c55e' }">
              {{ result.group_indices?.unity.toFixed(2) }}%
            </div>
          </div>
          <div class="index-card">
            <div class="index-label">Индекс групповой разобщенности (Игр)</div>
            <div class="index-value" :style="{ color: '#ef4444' }">
              {{ result.group_indices?.division.toFixed(2) }}%
            </div>
          </div>
          <div class="index-card">
            <div class="index-label">Индекс групповой сплоченности (Игс)</div>
            <div class="index-value" :style="{ color: '#2563eb' }">
              {{ result.group_indices?.cohesion.toFixed(2) }}%
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- TAB: Классификация -->
    <div v-if="activeResultTab === 'classification'" class="result-content">
      <h3>Классификация участников</h3>
      
      <div class="classification-grid">
        <div 
          v-for="(group, groupName) in result.classification"
          :key="groupName"
          class="classification-group"
          :class="`group-${groupName}`"
        >
          <h4 class="group-title">
            {{ groupLabels[groupName] }} ({{ group.length }})
          </h4>
          <div class="group-members">
            <div 
              v-for="member in group"
              :key="member.id"
              class="member-badge"
            >
              {{ member.name }}
            </div>
          </div>
        </div>
      </div>

      <div class="classification-description">
        <p><strong>Лидеры (звезды):</strong> Получили выборы ≥ 2 × среднее значение</p>
        <p><strong>Предпочитаемые:</strong> Получили выборы > среднее значение</p>
        <p><strong>Принятые:</strong> Получили выборы < среднего значения</p>
        <p><strong>Изолированные:</strong> Не получили ни + ни - выборов</p>
        <p><strong>Пренебрегаемые:</strong> К+п = 0, но К-п ≥ 1</p>
        <p><strong>Аутсайдеры:</strong> Только отрицательные выборы</p>
      </div>
    </div>

    <!-- TAB: Социограмма -->
    <div v-if="activeResultTab === 'sociogram'" class="result-content">
      <h3>Социограмма</h3>

      <div class="sociogram-wrap">
        <div class="sociogram-scroll">
          <svg
            class="sociogram"
            :width="sociogramLayout.size"
            :height="sociogramLayout.size"
            :viewBox="`0 0 ${sociogramLayout.size} ${sociogramLayout.size}`"
            role="img"
            aria-label="Социограмма"
          >
            <defs>
              <marker
                id="arrowPlus"
                viewBox="0 0 10 10"
                refX="9"
                refY="5"
                markerWidth="6"
                markerHeight="6"
                orient="auto-start-reverse"
              >
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#22c55e" />
              </marker>
              <marker
                id="arrowMinus"
                viewBox="0 0 10 10"
                refX="9"
                refY="5"
                markerWidth="6"
                markerHeight="6"
                orient="auto-start-reverse"
              >
                <path d="M 0 0 L 10 5 L 0 10 z" fill="#ef4444" />
              </marker>
            </defs>

            <!-- Кольца -->
            <g class="sociogram-rings">
              <circle
                v-for="(r, i) in sociogramLayout.rings"
                :key="i"
                :cx="sociogramLayout.center.x"
                :cy="sociogramLayout.center.y"
                :r="r"
                class="sociogram-ring"
              />
            </g>

            <!-- Стрелки -->
            <g class="sociogram-edges">
              <path
                v-for="e in sociogramEdges"
                :key="e.key"
                :d="edgePath(e)"
                :class="['sociogram-edge', `sociogram-edge--${e.type}`]"
                :marker-end="e.type === 'plus' ? 'url(#arrowPlus)' : 'url(#arrowMinus)'"
              />
            </g>

            <!-- Узлы -->
            <g class="sociogram-nodes">
              <g
                v-for="n in sociogramNodes"
                :key="n.id"
                class="sociogram-node"
                :transform="`translate(${n.x}, ${n.y})`"
              >
                <title>{{ n.full_name }}</title>
                <circle :r="sociogramLayout.nodeRadius" class="sociogram-node__circle" />
                <text class="sociogram-node__text" text-anchor="middle" dominant-baseline="middle">
                  {{ n.label }}
                </text>
              </g>
            </g>
          </svg>
        </div>

        <div class="sociogram-legend">
          <div class="sociogram-legend__row">
            <span class="legend-dot legend-dot--plus"></span>
            <span>Положительный выбор (+)</span>
          </div>
          <div class="sociogram-legend__row">
            <span class="legend-dot legend-dot--minus"></span>
            <span>Отрицательный выбор (-)</span>
          </div>
          <div class="sociogram-legend__hint">
            Номера в кружках соответствуют списку участников ниже.
          </div>
        </div>

        <div class="sociogram-names">
          <div v-for="n in sociogramNodes" :key="`name-${n.id}`" class="sociogram-names__item">
            <span class="sociogram-names__num">{{ n.label }}.</span>
            <span>{{ n.full_name }}</span>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
export default {
  name: 'StarChartResults',
  props: {
    result: {
      type: Object,
      required: true,
    },
    survey: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      activeResultTab: 'indices',
      tabLabels: {
        indices: 'Индексы',
        classification: 'Классификация',
        sociogram: 'Социограмма',
      },
      groupLabels: {
        leaders: 'Лидеры (звезды)',
        preferred: 'Предпочитаемые',
        accepted: 'Принятые',
        isolated: 'Изолированные',
        neglected: 'Пренебрегаемые',
        outsiders: 'Аутсайдеры',
      },
    }
  },
  computed: {
    notPassed() {
      return (this.result && Array.isArray(this.result.not_passed)) ? this.result.not_passed : []
    },
    participants() {
      const list = this.survey?.participants
      return Array.isArray(list) ? list : []
    },

    sociogramLayout() {
      return {
        size: 520,
        center: { x: 260, y: 260 },
        rings: [70, 140, 210, 250],
        nodeRadius: 16,
      }
    },

    participantNumbers() {
      const map = {}
      this.participants.forEach((p, idx) => {
        map[String(p.id)] = String(idx + 1)
      })
      return map
    },

    ringByParticipantId() {
      const groupToRing = {
        leaders: 0,
        preferred: 1,
        accepted: 2,
        isolated: 3,
        neglected: 3,
        outsiders: 3,
      }

      const out = {}
      const classification = this.result?.classification
      if (!classification || typeof classification !== 'object') return out

      Object.entries(classification).forEach(([groupName, members]) => {
        const ring = groupToRing[groupName]
        if (ring == null) return
        if (!Array.isArray(members)) return
        members.forEach((m) => {
          if (m && m.id != null) out[String(m.id)] = ring
        })
      })

      return out
    },

    sociogramNodes() {
      const layout = this.sociogramLayout
      const center = layout.center
      const ringById = this.ringByParticipantId
      const rings = [[], [], [], []]

      this.participants.forEach((p) => {
        const id = String(p.id)
        const ringIndex = ringById[id] != null ? ringById[id] : 2
        rings[ringIndex].push(p)
      })

      const nodes = []
      const startAngle = -Math.PI / 2
      const leaderInnerRadius = 28

      rings.forEach((list, ringIndex) => {
        if (!list.length) return

        const radius = ringIndex === 0
          ? (list.length === 1 ? 0 : leaderInnerRadius)
          : layout.rings[ringIndex]

        list.forEach((p, idx) => {
          const angle = list.length === 1
            ? startAngle
            : (startAngle + (idx * 2 * Math.PI) / list.length)
          const x = center.x + radius * Math.cos(angle)
          const y = center.y + radius * Math.sin(angle)

          nodes.push({
            id: p.id,
            x,
            y,
            full_name: p.full_name,
            label: this.participantNumbers[String(p.id)] || '',
            ring: ringIndex,
          })
        })
      })

      return nodes
    },

    sociogramNodeMap() {
      const map = {}
      this.sociogramNodes.forEach((n) => {
        map[String(n.id)] = n
      })
      return map
    },

    sociogramEdges() {
      const matrix = this.result?.sociomatrix
      if (!matrix || typeof matrix !== 'object') return []

      const nodeMap = this.sociogramNodeMap
      const seen = new Set()
      const edges = []

      Object.entries(matrix).forEach(([ridRaw, row]) => {
        if (!row || typeof row !== 'object') return
        const rid = String(ridRaw)
        if (!nodeMap[rid]) return

        Object.entries(row).forEach(([cidRaw, val]) => {
          const cid = String(cidRaw)
          if (rid === cid) return
          if (!nodeMap[cid]) return
          if (val == null) return

          const tokens = String(val).match(/[+-]\d+/g) || []
          const hasPlus = tokens.some((t) => t.startsWith('+'))
          const hasMinus = tokens.some((t) => t.startsWith('-'))

          if (hasPlus) {
            const key = `${rid}-${cid}-plus`
            if (!seen.has(key)) {
              seen.add(key)
              edges.push({ key, from: rid, to: cid, type: 'plus' })
            }
          }
          if (hasMinus) {
            const key = `${rid}-${cid}-minus`
            if (!seen.has(key)) {
              seen.add(key)
              edges.push({ key, from: rid, to: cid, type: 'minus' })
            }
          }
        })
      })

      return edges
    },
  },
  methods: {
    formatDate(date) {
      return new Date(date).toLocaleDateString('ru-RU')
    },
    getIndexClass(value) {
      if (value > 50) return 'positive'
      if (value < -50) return 'negative'
      return 'neutral'
    },

    edgePath(edge) {
      const layout = this.sociogramLayout
      const nodeMap = this.sociogramNodeMap
      const from = nodeMap[String(edge.from)]
      const to = nodeMap[String(edge.to)]
      if (!from || !to) return ''

      const dx = to.x - from.x
      const dy = to.y - from.y
      const dist = Math.hypot(dx, dy) || 1

      const ux = dx / dist
      const uy = dy / dist

      const startX = from.x + ux * layout.nodeRadius
      const startY = from.y + uy * layout.nodeRadius
      const endX = to.x - ux * layout.nodeRadius
      const endY = to.y - uy * layout.nodeRadius

      // Straight line (previously used quadratic curve via `Q`).
      return `M ${startX} ${startY} L ${endX} ${endY}`
    },
  },
}
</script>

<style scoped>
.results-container {
  background: white;
  border-radius: 8px;
  padding: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.results-header {
  margin-bottom: 2rem;
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 1rem;
}

.results-header h2 {
  margin: 0 0 0.5rem 0;
  color: #1a202c;
}

.result-meta {
  display: flex;
  gap: 2rem;
  font-size: 0.9rem;
  color: #6b7280;
}

.result-tabs {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 2rem;
  border-bottom: 2px solid #e5e7eb;
  flex-wrap: wrap;
}

.not-passed {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1rem 1.25rem;
  margin-bottom: 1.5rem;
}

.not-passed__label {
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
}

.not-passed__list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.not-passed__chip {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 9999px;
  padding: 0.35rem 0.75rem;
  font-size: 0.9rem;
  color: #111827;
}

.result-tab-btn {
  padding: 0.75rem 1.5rem;
  background: none;
  border: none;
  border-bottom: 3px solid transparent;
  font-size: 0.95rem;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
}

.result-tab-btn:hover {
  color: #1a202c;
}

.result-tab-btn.active {
  color: #2563eb;
  border-bottom-color: #2563eb;
}

.result-content h3 {
  margin-top: 0;
  margin-bottom: 1.5rem;
  color: #1a202c;
  font-size: 1.1rem;
}

.sociogram-wrap {
  display: grid;
  gap: 1rem;
}

.sociogram-scroll {
  overflow-x: auto;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  background: #ffffff;
}

.sociogram {
  display: block;
  margin: 0 auto;
}

.sociogram-ring {
  fill: none;
  stroke: #d1d5db;
  stroke-width: 1.5;
}

.sociogram-edge {
  fill: none;
  stroke-width: 2;
  opacity: 0.9;
}

.sociogram-edge--plus {
  stroke: #22c55e;
}

.sociogram-edge--minus {
  stroke: #ef4444;
  stroke-dasharray: 6 4;
}

.sociogram-node__circle {
  fill: #ffffff;
  stroke: #111827;
  stroke-width: 1.5;
}

.sociogram-node__text {
  font-size: 11px;
  font-weight: 700;
  fill: #111827;
}

.sociogram-legend {
  background: #f9fafb;
  padding: 1rem;
  border-radius: 4px;
  font-size: 0.9rem;
  color: #6b7280;
}

.sociogram-legend__row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0.25rem 0;
}

.sociogram-legend__hint {
  margin-top: 0.5rem;
}

.legend-dot {
  width: 14px;
  height: 4px;
  border-radius: 9999px;
}

.legend-dot--plus {
  background: #22c55e;
}

.legend-dot--minus {
  background: #ef4444;
}

.sociogram-names {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 0.5rem 1rem;
  background: #f9fafb;
  padding: 1rem;
  border-radius: 4px;
}

.sociogram-names__item {
  display: flex;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: #111827;
}

.sociogram-names__num {
  font-weight: 700;
  color: #374151;
}

.indices-table {
  overflow-x: auto;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  margin-bottom: 2rem;
}

.indices-table table {
  width: 100%;
  border-collapse: collapse;
}

.indices-table th {
  background: #f3f4f6;
  padding: 0.75rem;
  text-align: left;
  font-weight: 600;
  font-size: 0.85rem;
  color: #374151;
  border-bottom: 2px solid #e5e7eb;
}

.indices-table td {
  padding: 0.75rem;
  border-bottom: 1px solid #e5e7eb;
}

.indices-table tr:hover {
  background: #f9fafb;
}

.name-cell {
  font-weight: 500;
  color: #1a202c;
}

.indices-table .positive {
  color: #22c55e;
  font-weight: 600;
}

.indices-table .negative {
  color: #ef4444;
  font-weight: 600;
}

.indices-table .neutral {
  color: #6b7280;
}

.indices-table .mutual {
  text-align: center;
}

.group-indices {
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 2px solid #e5e7eb;
}

.indices-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-top: 1rem;
}

.index-card {
  background: #f9fafb;
  padding: 1.5rem;
  border-radius: 8px;
  border-left: 4px solid #2563eb;
  text-align: center;
}

.index-label {
  font-size: 0.9rem;
  color: #6b7280;
  margin-bottom: 0.5rem;
}

.index-value {
  font-size: 2rem;
  font-weight: 700;
}

.classification-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.classification-group {
  padding: 1.5rem;
  border-radius: 8px;
  border: 2px solid #e5e7eb;
}

.classification-group.group-leaders {
  background: #fef3c7;
  border-color: #fcd34d;
}

.classification-group.group-preferred {
  background: #dbeafe;
  border-color: #bfdbfe;
}

.classification-group.group-accepted {
  background: #e0e7ff;
  border-color: #c7d2fe;
}

.classification-group.group-isolated {
  background: #f3f4f6;
  border-color: #d1d5db;
}

.classification-group.group-neglected {
  background: #fee2e2;
  border-color: #fecaca;
}

.classification-group.group-outsiders {
  background: #fed7aa;
  border-color: #fdba74;
}

.group-title {
  margin: 0 0 1rem 0;
  font-size: 1rem;
  font-weight: 600;
}

.group-members {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.member-badge {
  padding: 0.5rem 1rem;
  background: white;
  border-radius: 20px;
  font-size: 0.9rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.classification-description {
  background: #f9fafb;
  padding: 1.5rem;
  border-radius: 4px;
  font-size: 0.9rem;
  line-height: 1.6;
}

.classification-description p {
  margin: 0.5rem 0;
}

.classification-description strong {
  color: #1a202c;
}
</style>